import React, { useRef, useState } from "react";
import { api, apiError, BACKEND_URL } from "../lib/api";
import { uploadToDropbox } from "../lib/dropboxUpload";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Upload,
  FileVideo,
  Trash2,
  X,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";

const MAX_MB = 2000;

function prettyBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function EditedUploadDialog({
  open,
  onOpenChange,
  messageId,
  groupId,
  fileName,
  clientName,
  existing,
  onUpdated,
}) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  React.useEffect(() => {
    if (!open) {
      setFile(null);
      setProgress(0);
      setUploading(false);
    }
  }, [open]);

  const pick = () => inputRef.current?.click();

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File troppo grande (max ${MAX_MB}MB)`);
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File troppo grande (max ${MAX_MB}MB)`);
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const data = await uploadToDropbox({
        kind: groupId ? "lavorato_gruppo" : "lavorato",
        file,
        targetId: groupId || messageId,
        onProgress: (p) => setProgress(p),
      });
      const path = data?.edited_asset?.dropbox_path || "";
      toast.success(
        path
          ? `Caricato in Dropbox: ${path}`
          : "Versione lavorata caricata su Dropbox",
        { duration: 8000 }
      );
      onUpdated && onUpdated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || "Errore durante l'upload");
    } finally {
      setUploading(false);
    }
  };

  const removeExisting = async () => {
    if (!existing) return;
    if (
      !window.confirm(
        "Rimuovere la versione lavorata? Il file resterà su Dropbox ma non sarà più collegato al contenuto."
      )
    )
      return;
    try {
      const endpoint = groupId
        ? `/staff/groups/${groupId}/edited-upload`
        : `/staff/tasks/${messageId}/edited-upload`;
      await api.delete(endpoint);
      toast.success("Collegamento rimosso");
      onUpdated && onUpdated();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="edited-upload-dialog"
        className="max-w-lg rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Upload className="h-5 w-5" /> Carica versione lavorata
          </DialogTitle>
          <DialogDescription>
            {clientName ? `${clientName} · ` : ""}
            {fileName} · salva su Dropbox nella cartella "Lavorati".
          </DialogDescription>
        </DialogHeader>

        {existing ? (
          <div
            data-testid="edited-existing-card"
            className="rounded-2xl border border-[var(--mp-ink)] bg-[var(--mp-dash-bg)] p-4 space-y-2"
          >
            <div className="flex items-center gap-2 text-[var(--mp-ink)]">
              <CheckCircle2 className="h-5 w-5" />
              <div className="font-display text-base tracking-tight">
                Versione lavorata caricata
              </div>
            </div>
            <div className="text-[12px] text-[var(--mp-muted)] truncate">
              {existing.file_name} · {prettyBytes(existing.size)}
            </div>
            <div className="flex items-center gap-2 pt-2 flex-wrap">
              <a
                href={`${BACKEND_URL}${existing.dropbox_url}`}
                target="_blank"
                rel="noreferrer"
                data-testid="edited-open-link"
                className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-[var(--mp-ink)] text-white text-sm hover:bg-black"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Apri su Dropbox
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={removeExisting}
                data-testid="edited-remove-btn"
                className="rounded-full border-[var(--mp-primary)] text-[var(--mp-primary)]"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Scollega
              </Button>
            </div>
            <div className="pt-3 border-t border-[var(--mp-border)] text-[11px] text-[var(--mp-muted)]">
              Sostituisci caricando un nuovo file:
            </div>
          </div>
        ) : null}

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={pick}
          data-testid="edited-dropzone"
          className={`rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
            file
              ? "border-[var(--mp-ink)] bg-[var(--mp-dash-bg)]"
              : "border-[var(--mp-border)] hover:border-[var(--mp-ink)]"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="video/*,image/*"
            className="hidden"
            onChange={onFileChange}
            data-testid="edited-file-input"
          />
          {file ? (
            <div className="space-y-1">
              <FileVideo className="h-8 w-8 mx-auto" />
              <div className="font-display text-base tracking-tight truncate">
                {file.name}
              </div>
              <div className="text-[11px] text-[var(--mp-muted)]">
                {prettyBytes(file.size)}
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <Upload className="h-8 w-8 mx-auto text-[var(--mp-muted)]" />
              <div className="font-display text-base tracking-tight">
                Trascina qui il file o clicca per sceglierlo
              </div>
              <div className="text-[11px] text-[var(--mp-muted)]">
                Video o immagine · max {MAX_MB}MB
              </div>
            </div>
          )}
        </div>

        {uploading && (
          <div data-testid="edited-progress" className="space-y-1">
            <div className="h-2 rounded-full bg-[var(--mp-dash-bg)] overflow-hidden">
              <div
                className="h-full bg-[var(--mp-ink)] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-[11px] text-[var(--mp-muted)]">
              Caricamento… {progress}%
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--mp-border)]">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={uploading}
            className="rounded-full border-[var(--mp-ink)]"
            data-testid="edited-cancel"
          >
            <X className="h-4 w-4 mr-1" /> Chiudi
          </Button>
          <Button
            onClick={upload}
            disabled={!file || uploading}
            data-testid="edited-upload-btn"
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Upload className="h-4 w-4 mr-1" />
            {uploading ? "Carico…" : "Carica su Dropbox"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
